import { TeamsApi, type APIQueryOptions, type TeamAdminResponse } from "@xs2event/js-lib/api";
import { useQuery, type QueryClient, type UseQueryOptions } from "@tanstack/vue-query";
import { queryClient as defaultQueryClient } from "@/queryClient";

const teamsApi = new TeamsApi();

// The API caps `pageSize` well below the size of the full catalog, so it can't be fetched in one
// request — page through it at the max page size instead of asking for e.g. `pageSize: 9999`.
const PAGE_SIZE = 1000;

const TEAMS_CATALOG_QUERY_KEY = ["teamsGet", "all"];

/**
 * Long enough that one fetch covers a typical browsing session, mirroring
 * `PORTAL_EVENTS_CACHE_STALE_TIME` (`@/composables/events`).
 */
export const TEAMS_CATALOG_STALE_TIME = 1000 * 60 * 30; // 30 minutes

/**
 * Fetches the entire teams catalog by paging through it `PAGE_SIZE` at a time, following the
 * response's pagination metadata — same "fetch everything" approach `useApiListQuery`'s `pageSize:
 * "all"` mode uses internally, reimplemented here (rather than going through that composable) so
 * `useTeamsCatalog`, `getTeamBySlug` and `prefetchTeamsCatalog` can all share one explicit,
 * predictable query key instead of relying on that composable's own internal key format.
 */
const fetchTeamsCatalog = async (): Promise<TeamAdminResponse[]> => {
  let teams: TeamAdminResponse[] = [];
  let page = 1;
  for (;;) {
    const response = await teamsApi.teamsGet({ page, pageSize: PAGE_SIZE });
    teams = teams.concat(response.teams);

    const { totalSize, nextPage } = response.pagination ?? {};
    const hasMore = nextPage
      ? true
      : totalSize != null
        ? teams.length < totalSize
        : response.teams.length === PAGE_SIZE;
    if (!hasMore || response.teams.length === 0) break;
    page += 1;
  }
  return teams;
};

/**
 * The entire teams catalog. Backs the events listing's team filter facet (`Team.vue`) and team-slug
 * resolution (`TeamInfo.vue`, `AllEventsPage.vue`). One shared, rarely-changing list fetched once and
 * reused everywhere, rather than every consumer fetching its own copy — mirrors
 * `usePortalEventsCache`/`usePortalTournamentsCache`.
 */
export const useTeamsCatalog = (options?: APIQueryOptions) =>
  useQuery({
    queryKey: TEAMS_CATALOG_QUERY_KEY,
    queryFn: fetchTeamsCatalog,
    staleTime: TEAMS_CATALOG_STALE_TIME,
    ...options,
  } as UseQueryOptions<TeamAdminResponse[]>);

/** Find a team in an already-fetched catalog by its slug. */
export const findTeamBySlug = (teams: TeamAdminResponse[] | undefined, slug: string | undefined | null) =>
  slug ? teams?.find((team) => team.slug === slug) : undefined;

/**
 * Resolve a team by slug from outside a component — router navigation guards
 * (`src/pages/index.ts`) and Pinia store actions (`src/store/events.js`) can't call
 * `useTeamsCatalog()`/`useQueryClient()` since there's no active component/effect scope there.
 * Reads the shared catalog from cache if still fresh, otherwise fetches it once (shared with every
 * other consumer of `useTeamsCatalog`).
 */
export const getTeamBySlug = async (slug: string | undefined | null, client: QueryClient = defaultQueryClient) => {
  if (!slug) return undefined;
  const teams = await client.ensureQueryData({
    queryKey: TEAMS_CATALOG_QUERY_KEY,
    queryFn: fetchTeamsCatalog,
    staleTime: TEAMS_CATALOG_STALE_TIME,
  });
  return findTeamBySlug(teams, slug);
};

/** Warm the teams catalog from outside a component — see `prefetchPortalEventsCache` (`@/composables/events`). */
export const prefetchTeamsCatalog = (client: QueryClient = defaultQueryClient) =>
  client.prefetchQuery({
    queryKey: TEAMS_CATALOG_QUERY_KEY,
    queryFn: fetchTeamsCatalog,
    staleTime: TEAMS_CATALOG_STALE_TIME,
  });
